import type {
  Game,
  GameMode,
  Round,
  RoundScore,
  ParticipantTotal,
  ValidationResult,
} from '@/types';
import { SCORING } from '@/types';

/**
 * Calculate total scores for all participants in a game
 */
export function calculateTotals(
  participants: Game['participants'],
  rounds: Round[]
): ParticipantTotal[] {
  const totals: Map<string, ParticipantTotal> = new Map();

  // Initialize totals for all participants
  for (const participant of participants) {
    totals.set(participant.id, {
      participantId: participant.id,
      participantName: participant.name,
      regularPoints: 0,
      declarationPoints: 0,
      totalPoints: 0,
    });
  }

  // Sum up scores from all rounds
  for (const round of rounds) {
    for (const score of round.scores) {
      const total = totals.get(score.participantId);
      if (total) {
        total.regularPoints += score.regularPoints;
        total.declarationPoints += score.declarationPoints;
        total.totalPoints += score.regularPoints + score.declarationPoints;
      }
    }
  }

  return Array.from(totals.values());
}

/**
 * Check if there's a winner based on target score
 */
export function checkWinner(
  totals: ParticipantTotal[],
  targetScore: number
): string | null {
  for (const total of totals) {
    if (total.totalPoints >= targetScore) {
      return total.participantId;
    }
  }
  return null;
}

/**
 * Get the leading participant
 */
export function getLeader(totals: ParticipantTotal[]): ParticipantTotal | null {
  if (totals.length === 0) return null;

  return totals.reduce((leader, current) =>
    current.totalPoints > leader.totalPoints ? current : leader
  );
}

/**
 * Validate round scores based on game mode
 */
export function validateRoundScores(
  mode: GameMode,
  scores: RoundScore[]
): ValidationResult {
  const errors: string[] = [];

  // Validate each score individually
  for (const score of scores) {
    if (score.regularPoints < 0) {
      errors.push('Regular points cannot be negative');
    }
    if (score.regularPoints > SCORING.MAX_REGULAR_POINTS) {
      errors.push(`Regular points cannot exceed ${SCORING.MAX_REGULAR_POINTS}`);
    }
    if (score.declarationPoints < 0) {
      errors.push('Declaration points cannot be negative');
    }
    if (score.declarationPoints > SCORING.MAX_DECLARATION_POINTS) {
      errors.push(
        `Declaration points cannot exceed ${SCORING.MAX_DECLARATION_POINTS}`
      );
    }
  }

  // Validate sum constraint only for 4-player mode
  // In 3-player mode, after inside rule is applied, total won't be 162
  // In 2-player mode, points are entered independently
  if (mode === '4_player') {
    const regularSum = scores.reduce((sum, s) => sum + s.regularPoints, 0);
    if (regularSum !== SCORING.POINTS_SUM_CONSTRAINT) {
      errors.push(
        `Regular points must sum to exactly ${SCORING.POINTS_SUM_CONSTRAINT} (currently ${regularSum})`
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Calculate points distribution for when one player/team gets all points (162-0 or equivalent)
 */
export function calculateCapotPoints(
  _mode: GameMode,
  winnerId: string,
  loserId: string | string[]
): RoundScore[] {
  const scores: RoundScore[] = [
    {
      participantId: winnerId,
      regularPoints: SCORING.MAX_REGULAR_POINTS,
      declarationPoints: 0,
    },
  ];

  const loserIds = Array.isArray(loserId) ? loserId : [loserId];
  for (const id of loserIds) {
    scores.push({
      participantId: id,
      regularPoints: 0,
      declarationPoints: 0,
    });
  }

  return scores;
}

/**
 * Get progress percentage toward target score
 */
export function getProgressPercentage(
  currentPoints: number,
  targetScore: number
): number {
  return Math.min(100, Math.round((currentPoints / targetScore) * 100));
}

/**
 * Format points for display
 */
export function formatPoints(points: number): string {
  return points.toLocaleString();
}

/**
 * Apply inside/outside rule for trump scoring.
 *
 * For 2-player mode:
 *   If trump caller's total points <= opponent's total points,
 *   trump caller gets 0 and opponent gets their own points + trump caller's points.
 *
 * For 3-player and 4-player modes:
 *   If trump caller's total points <= half of round total (162 + declarations),
 *   they get 0 and opponents get all points distributed evenly.
 *
 * @param scores - The raw scores before applying the rule
 * @param trumpCallerId - The participant who called trump
 * @param mode - The game mode
 * @returns Modified scores with inside/outside rule applied, plus a flag indicating if rule was applied
 */
export function applyInsideOutsideRule(
  scores: RoundScore[],
  trumpCallerId: string,
  mode: GameMode
): { scores: RoundScore[]; insideApplied: boolean } {
  const trumpCallerScore = scores.find((s) => s.participantId === trumpCallerId);
  if (!trumpCallerScore) {
    return { scores, insideApplied: false };
  }

  const trumpCallerTotal =
    trumpCallerScore.regularPoints + trumpCallerScore.declarationPoints;

  if (mode === '2_player') {
    // 2-player mode: compare trump caller with opponent directly
    const opponentScore = scores.find((s) => s.participantId !== trumpCallerId);
    if (!opponentScore) {
      return { scores, insideApplied: false };
    }

    const opponentTotal =
      opponentScore.regularPoints + opponentScore.declarationPoints;

    // If trump caller's total <= opponent's total, they go "inside"
    if (trumpCallerTotal <= opponentTotal) {
      const modifiedScores = scores.map((s) => {
        if (s.participantId === trumpCallerId) {
          return { ...s, regularPoints: 0, declarationPoints: 0 };
        } else {
          // Opponent gets their own points + trump caller's points
          return {
            ...s,
            regularPoints: opponentScore.regularPoints + trumpCallerScore.regularPoints,
            declarationPoints: opponentScore.declarationPoints + trumpCallerScore.declarationPoints,
          };
        }
      });
      return { scores: modifiedScores, insideApplied: true };
    }
  } else if (mode === '3_player') {
    // 3-player mode: compare trump caller with combined total of other two players
    const opponents = scores.filter((s) => s.participantId !== trumpCallerId);
    const opponentsCombinedTotal = opponents.reduce(
      (sum, s) => sum + s.regularPoints + s.declarationPoints,
      0
    );

    // If trump caller's total <= combined opponents' total, they go "inside"
    if (trumpCallerTotal <= opponentsCombinedTotal) {
      const modifiedScores = scores.map((s) => {
        if (s.participantId === trumpCallerId) {
          return { ...s, regularPoints: 0, declarationPoints: 0 };
        } else {
          // Other players keep their own points
          return s;
        }
      });
      return { scores: modifiedScores, insideApplied: true };
    }
  } else {
    // 4-player mode: compare with half of total (162 + declarations)
    const totalRegular = SCORING.MAX_REGULAR_POINTS;
    const totalDeclarations = scores.reduce(
      (sum, s) => sum + s.declarationPoints,
      0
    );
    const totalPoints = totalRegular + totalDeclarations;
    const halfTotal = totalPoints / 2;

    // If trump caller scores <= half, they lose everything (went "inside")
    if (trumpCallerTotal <= halfTotal) {
      const modifiedScores = scores.map((s) => {
        if (s.participantId === trumpCallerId) {
          return { ...s, regularPoints: 0, declarationPoints: 0 };
        } else {
          // Opponent team gets all points
          return {
            ...s,
            regularPoints: totalRegular,
            declarationPoints: totalDeclarations,
          };
        }
      });
      return { scores: modifiedScores, insideApplied: true };
    }
  }

  return { scores, insideApplied: false };
}
